import { useCallback, useEffect, useRef, useState } from "react";
import { KeyRound, LogIn, LogOut, WifiOff } from "lucide-react";
import App from "./App";
import { PasswordChangeForm } from "./AccountViews";
import { StudentDashboard } from "./StudentDashboard";
import { SchoolLevel } from "./catalog";
import {competenceoBuildId, shortBuildId} from "./build-info";

export type AuthUser = { id: string; role: "admin" | "teacher" | "student"; email?: string; studentCode?: string; fullName: string; classId?: string; studentId?: string };
export type SyncReport = { revision: number; classCount: number; roomCount: number };

type StudentPayload = {
  classId: string;
  className: string;
  schoolLevel: SchoolLevel;
  student: { id: string; firstName: string; lastName: string; sex?: "homme" | "femme"; photoUrl?: string; results: Record<string, "A" | "B" | "C" | "D">; resultUpdatedAt?: Record<string, string> };
  catalog: Parameters<typeof StudentDashboard>[0]["catalog"];
  activities: Parameters<typeof StudentDashboard>[0]["activities"];
  sessions: Parameters<typeof StudentDashboard>[0]["sessions"];
};

class ApiError extends Error {
  status: number;
  payload: Record<string, unknown>;
  constructor(status: number, payload: Record<string, unknown>) {
    super(String(payload.error ?? "Une erreur est survenue"));
    this.status = status;
    this.payload = payload;
  }
}

async function api(path: string, options?: RequestInit) {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }, ...options });
  const body = await response.json();
  if (!response.ok) throw new ApiError(response.status, body);
  return body;
}

function avatarForStudent(student: StudentPayload["student"]) {
  if (student.photoUrl) return student.photoUrl;
  const sex = student.sex ?? "homme";
  const hash = [...student.id].reduce((total, character) => total + character.charCodeAt(0), 0);
  return `/avatars/${sex}/${sex}-${String((hash % 6) + 1).padStart(2, "0")}.webp`;
}

function Login({ onLogin }: { onLogin: (user: AuthUser) => void }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try { onLogin((await api("/api/auth/login", { method: "POST", body: JSON.stringify({ identifier, password }) })).user); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Connexion impossible"); }
    finally { setBusy(false); }
  }
  return <main className="login-page"><section className="login-card"><div className="login-brand"><span>CO</span><div><strong>CompetenceO</strong><small>Suivi pédagogique</small></div></div><div className="login-title"><KeyRound size={25} /><div><h1>Connexion</h1><p>Email professeur ou code élève</p></div></div><form onSubmit={submit}><label><span>Email ou code</span><input autoFocus autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} required /></label><label><span>Mot de passe</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>{error && <p className="login-error">{error}</p>}<button className="primary" disabled={busy}><LogIn size={17} />{busy ? "Connexion…" : "Se connecter"}</button></form></section></main>;
}

function StudentSpace({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const [payload, setPayload] = useState<StudentPayload | null>(null);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => { api("/api/student/dashboard").then(setPayload).catch((reason) => setError(reason.message)); }, []);
  if (error) return <main className="portal-state"><WifiOff size={30} /><h1>Fiche indisponible</h1><p>{error}</p><button className="secondary" onClick={onLogout}><LogOut size={16} /> Se déconnecter</button></main>;
  if (!payload) return <main className="portal-state"><div className="loading-ring" /><p>Chargement de ta fiche…</p></main>;
  return <div className="student-space"><header className="student-space-bar"><div><span>CO</span><strong>Espace élève · {user.fullName}</strong><small className="student-build-id" title={competenceoBuildId}>Build {shortBuildId()}</small></div><div className="student-account-actions"><button onClick={() => setShowPassword((value) => !value)}><KeyRound size={16} /> Mot de passe</button><button onClick={onLogout}><LogOut size={16} /> Se déconnecter</button></div></header>{showPassword && <PasswordChangeForm compact onDone={() => setShowPassword(false)} />}<StudentDashboard {...payload} avatarUrl={avatarForStudent(payload.student)} onBack={() => {}} hideBack /></div>;
}

function localData() {
  const data: Record<string, string> = {};
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith("competenceo-")) data[key] = localStorage.getItem(key) ?? "";
  }
  return data;
}

const sharedKeys = new Set([
  "competenceo-classes-v1",
  "competenceo-rooms-v1",
  "competenceo-catalog-v3",
  "competenceo-activities-v3",
  "competenceo-sessions-v1",
  "competenceo-current-session-v1",
  "competenceo-teacher-levels-v1",
]);

function isSharedKey(key: string) {
  return sharedKeys.has(key) || key.startsWith("competenceo-students-v1:") || key.startsWith("competenceo-plan-v1:");
}

function clearLocalData() {
  Object.keys(localStorage).filter((key) => key.startsWith("competenceo-")).forEach((key) => localStorage.removeItem(key));
}

type DataSnapshot = Record<string, string>;

function canonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJson);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, canonicalJson(child)]),
    );
  }
  return value;
}

function canonicalValue(value: unknown) {
  const text = String(value);
  try { return JSON.stringify(canonicalJson(JSON.parse(text))); }
  catch { return text; }
}

function normalizedSnapshot(data: Record<string, unknown> | undefined): DataSnapshot {
  return Object.fromEntries(Object.entries(data ?? {}).map(([key, value]) => [key, canonicalValue(value)]));
}

function sharedSnapshot(data: Record<string, unknown> | undefined): DataSnapshot {
  return Object.fromEntries(Object.entries(normalizedSnapshot(data)).filter(([key]) => isSharedKey(key)));
}

function sharedLocalData() {
  return sharedSnapshot(localData());
}

function mergeJsonArrays(remoteValue: string, localValue: string) {
  try {
    const remote = JSON.parse(remoteValue);
    const local = JSON.parse(localValue);
    if (!Array.isArray(remote) || !Array.isArray(local) || ![...remote, ...local].every((item) => item && typeof item === "object" && "id" in item)) return localValue;
    const merged = new Map(remote.map((item) => [item.id, item]));
    local.forEach((item) => {
      const previous = merged.get(item.id);
      merged.set(item.id, previous ? {
        ...previous,
        ...item,
        results: previous.results || item.results ? { ...(previous.results ?? {}), ...(item.results ?? {}) } : undefined,
        resultUpdatedAt: previous.resultUpdatedAt || item.resultUpdatedAt ? { ...(previous.resultUpdatedAt ?? {}), ...(item.resultUpdatedAt ?? {}) } : undefined,
        participation: previous.participation || item.participation ? { ...(previous.participation ?? {}), ...(item.participation ?? {}) } : undefined,
        evaluations: previous.evaluations || item.evaluations ? { ...(previous.evaluations ?? {}), ...(item.evaluations ?? {}) } : undefined,
      } : item);
    });
    return JSON.stringify([...merged.values()]);
  } catch { return localValue; }
}

function mergeSnapshots(base: DataSnapshot, local: DataSnapshot, remote: DataSnapshot) {
  const merged: DataSnapshot = {};
  new Set([...Object.keys(base), ...Object.keys(local), ...Object.keys(remote)]).forEach((key) => {
    const original = base[key];
    const localValue = local[key];
    const remoteValue = remote[key];
    if (localValue === original) { if (remoteValue !== undefined) merged[key] = remoteValue; return; }
    if (remoteValue === original || remoteValue === undefined) { if (localValue !== undefined) merged[key] = localValue; return; }
    if (localValue !== undefined) merged[key] = mergeJsonArrays(remoteValue, localValue);
  });
  return merged;
}

function writeLocalData(data: DataSnapshot) {
  Object.entries(data).forEach(([key, value]) => localStorage.setItem(key, value));
}

function replaceSharedLocalData(data: DataSnapshot) {
  Object.keys(localStorage).filter(isSharedKey).forEach((key) => localStorage.removeItem(key));
  writeLocalData(data);
}

function sameSnapshot(left: DataSnapshot, right: DataSnapshot) {
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return keys.every((key) => left[key] === right[key]);
}

function storedCollectionCount(data: DataSnapshot, key: string) {
  try {
    const value = JSON.parse(data[key] ?? "[]");
    return Array.isArray(value) ? value.length : 0;
  } catch { return 0; }
}

export default function AuthApp() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);
  const [appVersion, setAppVersion] = useState(0);
  const revisionRef = useRef(0);
  const baseDataRef = useRef<DataSnapshot>({});
  const syncQueueRef = useRef<Promise<SyncReport | void>>(Promise.resolve());

  useEffect(() => { api("/api/auth/me").then((result) => setUser(result.user)).catch(() => setUser(null)).finally(() => setChecking(false)); }, []);
  useEffect(() => {
    if (!user || user.role === "student") { setReady(Boolean(user)); return; }
    setReady(false);
    api("/api/data").then(({ data, revision }) => {
      const serverData = sharedSnapshot(data);
      const hasServerData = data && Object.keys(data).length > 0;
      const existingLocalData = sharedLocalData();
      // Le premier compte administrateur récupère les données historiques du
      // navigateur. Tous les autres comptes démarrent dans un espace réellement
      // isolé afin qu'un professeur ne voie jamais les classes d'un autre.
      replaceSharedLocalData({});
      if (hasServerData) {
        // L'administrateur est aussi le compte professeur historique. On garde
        // ses plans locaux qui n'existent pas encore sur le serveur, sans jamais
        // remplacer une valeur déjà enregistrée en ligne.
        const hydratedData = user.role === "admin" ? { ...existingLocalData, ...serverData } : serverData;
        replaceSharedLocalData(hydratedData);
      } else if (user.role === "admin") {
        replaceSharedLocalData(existingLocalData);
      }
      revisionRef.current = Number(revision ?? 0);
      baseDataRef.current = serverData;
      setReady(true);
    }).catch(() => setReady(true));
  }, [user?.id]);

  async function logout() {
    try {
      if (user?.role !== "student") await sync();
      await api("/api/auth/logout", { method: "POST" });
      clearLocalData(); setUser(null); setReady(false);
    } catch (reason) {
      window.alert(reason instanceof Error ? `Déconnexion annulée : ${reason.message}. Tes données restent sur cet appareil.` : "Déconnexion annulée : sauvegarde impossible.");
    }
  }
  const sync = useCallback(() => {
    const run = async () => {
      const local = sharedLocalData();
      const latest = await api("/api/data");
      const remote = sharedSnapshot(latest.data);
      const remoteRevision = Number(latest.revision ?? 0);
      const remoteChanged = remoteRevision !== revisionRef.current || !sameSnapshot(remote, baseDataRef.current);
      let dataToSave = remoteChanged ? mergeSnapshots(baseDataRef.current, local, remote) : local;
      let savedRevision = remoteRevision;

      try {
        if (!sameSnapshot(dataToSave, remote)) {
          const result = await api("/api/data", { method: "PUT", body: JSON.stringify({ data: dataToSave, baseRevision: remoteRevision }) });
          savedRevision = Number(result.revision ?? remoteRevision + 1);
        }
      } catch (reason) {
        if (!(reason instanceof ApiError) || reason.status !== 409 || reason.payload.code !== "DATA_CONFLICT") throw reason;
        const conflictRemote = sharedSnapshot(reason.payload.data as Record<string, unknown>);
        const merged = mergeSnapshots(baseDataRef.current, dataToSave, conflictRemote);
        const result = await api("/api/data", { method: "PUT", body: JSON.stringify({ data: merged, baseRevision: Number(reason.payload.revision ?? 0) }) });
        dataToSave = merged;
        savedRevision = Number(result.revision ?? 0);
      }

      const confirmation = await api("/api/data");
      const confirmedData = sharedSnapshot(confirmation.data);
      if (!sameSnapshot(dataToSave, confirmedData)) {
        throw new Error("Le serveur n’a pas confirmé toutes les données envoyées");
      }
      dataToSave = confirmedData;
      savedRevision = Number(confirmation.revision ?? savedRevision);

      const receivedChanges = !sameSnapshot(dataToSave, local);
      if (receivedChanges) {
        replaceSharedLocalData(dataToSave);
        setAppVersion((version) => version + 1);
      }
      revisionRef.current = savedRevision;
      baseDataRef.current = dataToSave;
      return {
        revision: savedRevision,
        classCount: storedCollectionCount(dataToSave, "competenceo-classes-v1"),
        roomCount: storedCollectionCount(dataToSave, "competenceo-rooms-v1"),
      };
    };
    const queued = syncQueueRef.current.catch(() => undefined).then(run);
    syncQueueRef.current = queued;
    return queued;
  }, []);

  const exportData = useCallback(() => localData(), []);

  const restoreData = useCallback(async (data: DataSnapshot) => {
    if (!Object.entries(data).every(([key, value]) => key.startsWith("competenceo-") && typeof value === "string")) {
      throw new Error("Le contenu de la sauvegarde est invalide.");
    }
    const sharedData = sharedSnapshot(data);
    const result = await api("/api/data", { method: "PUT", body: JSON.stringify({ data: sharedData, baseRevision: revisionRef.current }) });
    clearLocalData();
    writeLocalData(data);
    revisionRef.current = Number(result.revision ?? revisionRef.current + 1);
    baseDataRef.current = sharedData;
    window.location.reload();
  }, []);

  if (checking) return <main className="portal-state"><div className="loading-ring" /></main>;
  if (!user) return <Login onLogin={setUser} />;
  if (user.role === "student") return <StudentSpace user={user} onLogout={logout} />;
  if (!ready) return <main className="portal-state"><div className="loading-ring" /><p>Chargement de ton espace…</p></main>;
  return <App key={appVersion} currentUser={user} onLogout={logout} onSync={sync} onExport={exportData} onRestore={restoreData} />;
}
